import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import { capitalize } from 'helpers/helpers';
import { trackEvent } from 'helpers/trackerHelpers';
import Button from '@mui/material/Button';
import validators from 'helpers/validators';
import PdfPreviewModal from 'components/PdfPreviewModal/PdfPreviewModal';
import SimpleModal from 'ui/Modals/SimpleModal';
import previewEmail from 'assets/preview_mail.png';
import { DashboardT } from 'business/definitions';
import DatePicker from 'components/inputs/DatePicker';
import {
    Recurrence,
    ScheduledDashboardEmail,
} from 'components/DashboardSendModal/ScheduledDashboardEmailConfigForm/types';
import { PdfPreviewText } from 'routes/Dashboard/components/PdfPreviewText';
import Tooltip from '@mui/material/Tooltip';
import StyWrapperButtonsCta from 'components/WrapperButtonsCta/WrapperButtonsCta';
import { useSelector } from 'store/hooks';
import { FormControlLabel, Switch } from '@mui/material';
import { useDebounce } from 'hooks/helpers';
import { AutocompleteUsers, UserOptionT } from './AutocompleteUsers';
import { useGetPossibleRecipients } from 'lib/alert/hooks/useFetchRecipients';

const StyForm = styled('form', {
    name: 'StyForm',
})(({ theme }) => ({
    '& .MuiTextField-root': {
        margin: theme.spacing(2),
        width: '80%',
    },
    'MuiDialogTitle-root': {
        padding: '16px 0',
    },
}));

const StyFormControlLabel = styled(FormControlLabel, {
    name: 'StyFormControlLabel',
})({
    display: 'block',
    margin: '16px',
});

const StyEmailTextField = styled(TextField, {
    name: 'StyEmailTextField',
})({
    marginLeft: '0.5rem',
    minWidth: '17rem',
});

type Props = {
    onSubmit: () => void;
    onGoBack: () => void;
    scheduledEmail: ScheduledDashboardEmail;
    setScheduledEmail: (arg1: ScheduledDashboardEmail) => void;
    dashboard: DashboardT;
};

const removeTime = (date: Date) => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
};

/*
 * Form handling the config of scheduled dashboard emails.
 */
const ScheduledDashboardEmailConfigForm: React.FC<Props> = ({
    onSubmit,
    onGoBack,
    scheduledEmail,
    setScheduledEmail,
    dashboard,
}) => {
    const { t } = useTranslation();
    const [emailError, setEmailError] = React.useState<string>('');
    const [openEmailPreview, setOpenEmailPreview] =
        React.useState<boolean>(false);
    const [openPDFPreview, setOpenPDFPreview] = React.useState<boolean>(false);

    const isSupportUser = useSelector((state) => state.vision.isSupportUser);
    const isEditing = scheduledEmail.id !== undefined;

    const { data: users = [], isLoading } = useGetPossibleRecipients(
        dashboard.key,
    );
    const usersEmail = users.map((user) => user.email);

    const [hasPendingChanges, setHasPendingChanges] =
        React.useState<boolean>(!isEditing);

    const initialFirstSentDate = useRef(scheduledEmail.config.firstSentDate);

    const handleRecipientsChange = (
        value: string[],
        configValue: ScheduledDashboardEmail,
    ) => {
        setScheduledEmail({
            ...configValue,
            config: {
                ...configValue.config,
                recipients: value,
            },
        });
    };

    const [debouncedScheduledText, setDebouncedScheduledText] = useState(
        scheduledEmail.config.text,
    );
    const setDebouncedText = useDebounce(
        () => setDebouncedScheduledText(scheduledEmail.config.text),
        250,
    );

    useEffect(() => {
        setDebouncedText();
    }, [scheduledEmail.config.text]);

    const previousScheduledEmail = useRef(scheduledEmail);

    useEffect(() => {
        if (!isEditing) return;

        const hasChanged =
            JSON.stringify(previousScheduledEmail.current.config) !==
            JSON.stringify(scheduledEmail.config);
        setHasPendingChanges(hasChanged);
    }, [
        isEditing,
        debouncedScheduledText,
        scheduledEmail.sender,
        scheduledEmail.config.recipients,
        scheduledEmail.config.firstSentDate,
        scheduledEmail.config.recurrence,
        scheduledEmail.config.shouldSendCopyToSender,
    ]);

    const showEmailPreview = () => setOpenEmailPreview(true);

    const validateInput = () => {
        if (
            scheduledEmail.config.recipients.length === 0 ||
            !scheduledEmail.config.recipients.every((mail) =>
                validators.isEmail(mail),
            )
        ) {
            setEmailError(t('validators.isEmail'));
            return false;
        }

        return true;
    };

    const recurrences = [];
    if (!isEditing)
        recurrences.push({
            value: 'once',
            label: t('sendDashboard.schedulingOnce'),
        });

    recurrences.push(
        ...[
            {
                value: 'daily',
                label: t('sendDashboard.schedulingDaily'),
            },
            {
                value: 'weekly',
                label: t('sendDashboard.schedulingWeekly'),
            },
            {
                value: 'monthly',
                label: t('sendDashboard.schedulingMonthly'),
            },
            {
                value: 'quarterly',
                label: t('sendDashboard.schedulingQuarterly'),
            },
        ],
    );
    const today = new Date();

    const isValidDate = useCallback(
        (date: string): boolean =>
            date === initialFirstSentDate.current ||
            removeTime(new Date(date)).getTime() >= removeTime(today).getTime(),
        [initialFirstSentDate],
    );
    const selectedDateValid = isValidDate(scheduledEmail.config.firstSentDate);

    return (
        <>
            <StyForm autoComplete="off">
                {isSupportUser && (
                    <div>
                        <AutocompleteUsers
                            allUsers={users}
                            disabled={isLoading}
                            sender={scheduledEmail.sender}
                            senderId={scheduledEmail.senderId}
                            onChange={(value: UserOptionT) =>
                                setScheduledEmail({
                                    ...scheduledEmail,
                                    sender: value.email,
                                    senderId: value.id,
                                })
                            }
                        />
                    </div>
                )}
                <div>
                    <Autocomplete
                        multiple
                        disabled={isLoading}
                        limitTags={2}
                        id="tags-filled"
                        options={usersEmail || []}
                        getOptionLabel={(option) => option}
                        filterSelectedOptions
                        value={scheduledEmail.config.recipients}
                        noOptionsText={t('sendDashboard.noOptions')}
                        onChange={(event, value) =>
                            handleRecipientsChange(value, scheduledEmail)
                        }
                        autoSelect
                        freeSolo
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const { key, ...extraProps } = getTagProps({
                                    index,
                                });
                                return (
                                    <Chip
                                        key={key}
                                        variant="outlined"
                                        label={option}
                                        {...extraProps}
                                    />
                                );
                            })
                        }
                        renderInput={(params) => (
                            <Tooltip
                                title={capitalize(
                                    t('sendDashboard.recipientTips'),
                                )}
                            >
                                <StyEmailTextField
                                    {...params}
                                    variant="outlined"
                                    label={capitalize(
                                        t('sendDashboard.recipients'),
                                    )}
                                    name="email"
                                    type="email"
                                    error={!!emailError}
                                    helperText={emailError}
                                />
                            </Tooltip>
                        )}
                    />
                </div>
                <div>
                    <TextField
                        variant="standard"
                        id="standard-multiline-static"
                        label={t('sendDashboard.formTextLabel')}
                        value={scheduledEmail.config.text}
                        placeholder={t('sendDashboard.initialText')}
                        onChange={(event) =>
                            setScheduledEmail({
                                ...scheduledEmail,
                                config: {
                                    ...scheduledEmail.config,
                                    text: event.target.value,
                                },
                            })
                        }
                        multiline
                        rows={7}
                    />
                </div>
                {/* TODO Make sure we can't select a date in the past */}
                <DatePicker
                    value={scheduledEmail.config.firstSentDate}
                    label={t('sendDashboard.sendDate')}
                    onChange={(inputDate) =>
                        setScheduledEmail({
                            ...scheduledEmail,
                            config: {
                                ...scheduledEmail.config,
                                firstSentDate: inputDate,
                            },
                        })
                    }
                    //if editing, minimum allowed date is today
                    min={today.toISOString().split('T')[0]}
                    isValidDateProp={isValidDate}
                    {...(selectedDateValid
                        ? {}
                        : {
                              error: true,
                              helperText: t('sendDashboard.futureDate'),
                          })}
                />
                <TextField
                    variant="standard"
                    id="recurrence-select"
                    label={t('sendDashboard.recurrence')}
                    value={scheduledEmail.config.recurrence}
                    SelectProps={{
                        // @ts-expect-error MUI type limitation : https://github.com/mui/material-ui/issues/34154
                        SelectDisplayProps: { 'data-testid': 'recurrence' },
                    }}
                    select
                    onChange={(event) =>
                        setScheduledEmail({
                            ...scheduledEmail,
                            config: {
                                ...scheduledEmail.config,
                                recurrence: event.target.value as Recurrence,
                            },
                        })
                    }
                >
                    {recurrences.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
                <StyFormControlLabel
                    control={
                        <Switch
                            checked={
                                scheduledEmail.config.shouldSendCopyToSender ===
                                    undefined ||
                                scheduledEmail.config.shouldSendCopyToSender
                            }
                            onChange={(event) =>
                                setScheduledEmail({
                                    ...scheduledEmail,
                                    config: {
                                        ...scheduledEmail.config,
                                        shouldSendCopyToSender:
                                            event.target.checked,
                                    },
                                })
                            }
                        />
                    }
                    label={t('sendDashboard.shouldSendCopyToSender')}
                />
                <Button onClick={showEmailPreview}>
                    {t('sendDashboard.mailPreview')}
                </Button>
                <Button
                    onClick={() => {
                        trackEvent(
                            'decision/dashboard/options/send/nouvel-envoi/aperçu-pdf-click',
                            {
                                dashboard: dashboard.key,
                            },
                        );
                        setOpenPDFPreview(true);
                    }}
                >
                    {t('sendDashboard.pdfPreview')}
                </Button>
                <StyWrapperButtonsCta>
                    <Button variant="outlined" onClick={onGoBack}>
                        {t('common.return')}
                    </Button>
                    <Button
                        disabled={!hasPendingChanges || !selectedDateValid}
                        variant="contained"
                        onClick={() => {
                            if (!validateInput()) return;
                            onSubmit();
                        }}
                    >
                        {isEditing
                            ? t('sendDashboard.edit')
                            : t('sendDashboard.continue')}
                    </Button>
                </StyWrapperButtonsCta>
            </StyForm>
            <SimpleModal
                open={openEmailPreview}
                onClose={() => setOpenEmailPreview(false)}
            >
                <div>
                    <p>{t('sendDashboard.mailTemplateMessage')}</p>
                    <img
                        src={previewEmail}
                        alt="email preview"
                        width="500"
                        height="600"
                    />
                </div>
            </SimpleModal>

            {openPDFPreview && (
                <PdfPreviewModal
                    open={openPDFPreview}
                    url={`explore/dashboard/${dashboard.key}/pdf`}
                    onClose={() => setOpenPDFPreview(false)}
                    title={t('sendDashboard.waitForPreview')}
                    downloadTitle={dashboard.title}
                    onSuccessText={<PdfPreviewText />}
                    shouldDisplayFixedWidthText={dashboard.isFullWidth}
                />
            )}
        </>
    );
};

export default ScheduledDashboardEmailConfigForm;
